<template>
  <div class="wl-state-picker" v-click-outside="closeDropdown">
    <input
      ref="input"
      v-model="searchQuery"
      type="text"
      role="combobox"
      aria-autocomplete="list"
      aria-controls="wl-state-list"
      :aria-expanded="isOpen ? 'true' : 'false'"
      autocomplete="off"
      :placeholder="placeholder"
      class="wl-state-input"
      :class="{ 'field-error': error }"
      @focus="onFocus"
      @input="onInput"
      @keydown.down.prevent="moveHighlight(1)"
      @keydown.up.prevent="moveHighlight(-1)"
      @keydown.enter.prevent="selectHighlighted"
      @keydown.esc="closeDropdown"
      @blur="onBlur"
    />
    <svg class="wl-state-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>

    <ul
      v-if="isOpen"
      id="wl-state-list"
      class="wl-state-list"
      role="listbox"
    >
      <li
        v-for="(item, idx) in filteredStates"
        :key="item.abbrev"
        role="option"
        :aria-selected="idx === highlightIndex ? 'true' : 'false'"
        :class="{ 'is-highlighted': idx === highlightIndex, 'is-selected': modelValue === item.abbrev }"
        @mousedown.prevent="selectState(item)"
      >
        <span class="wl-state-name">{{ item.name }}</span>
        <span class="wl-state-abbrev">{{ item.abbrev }}</span>
      </li>
      <li v-if="!filteredStates.length" class="wl-state-empty">
        No states match "{{ searchQuery }}"
      </li>
    </ul>
  </div>
</template>

<script>
import states from "@/utils/state.json";

export default {
  name: "WeightLossStatePicker",
  model: {
    prop: "modelValue",
    event: "update:modelValue"
  },
  props: {
    modelValue: {
      type: String,
      default: ""
    },
    error: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: "State..."
    }
  },
  emits: ["update:modelValue"],
  directives: {
    "click-outside": {
      bind(el, binding) {
        el._clickOutsideHandler = event => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener("mousedown", el._clickOutsideHandler);
        document.addEventListener("touchstart", el._clickOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener("mousedown", el._clickOutsideHandler);
        document.removeEventListener("touchstart", el._clickOutsideHandler);
        delete el._clickOutsideHandler;
      }
    }
  },
  data() {
    return {
      states: states,
      searchQuery: "",
      isOpen: false,
      highlightIndex: -1
    };
  },
  computed: {
    stateEntries() {
      return Object.entries(this.states).map(([name, abbrev]) => ({ name, abbrev }));
    },
    filteredStates() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.stateEntries;
      return this.stateEntries.filter(s =>
        s.name.toLowerCase().includes(q) ||
        s.abbrev.toLowerCase().includes(q)
      );
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        this.searchQuery = this.labelFor(val);
      }
    }
  },
  methods: {
    labelFor(abbrev) {
      if (!abbrev) return "";
      const match = this.stateEntries.find(s => s.abbrev === abbrev);
      return match ? `${match.name} (${match.abbrev})` : abbrev;
    },
    onFocus() {
      this.isOpen = true;
      this.highlightIndex = -1;
      // Clear text on focus so user can type fresh
      if (this.modelValue) {
        this.searchQuery = "";
      }
    },
    onInput() {
      this.isOpen = true;
      this.highlightIndex = -1;
      if (this.modelValue) {
        this.$emit("update:modelValue", "");
      }
    },
    onBlur() {
      // Restore label if user didn't pick anything
      this.$nextTick(() => {
        if (!this.isOpen) {
          this.searchQuery = this.labelFor(this.modelValue);
        }
      });
    },
    selectState(item) {
      this.$emit("update:modelValue", item.abbrev);
      this.searchQuery = `${item.name} (${item.abbrev})`;
      this.isOpen = false;
      this.highlightIndex = -1;
      if (this.$refs.input) this.$refs.input.blur();
    },
    moveHighlight(dir) {
      if (!this.isOpen) {
        this.isOpen = true;
        return;
      }
      const len = this.filteredStates.length;
      if (!len) return;
      const next = this.highlightIndex + dir;
      this.highlightIndex = (next + len) % len;
      this.$nextTick(() => this.scrollHighlightedIntoView());
    },
    selectHighlighted() {
      if (!this.isOpen) {
        this.isOpen = true;
        return;
      }
      const item = this.filteredStates[this.highlightIndex] || this.filteredStates[0];
      if (item) this.selectState(item);
    },
    closeDropdown() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.highlightIndex = -1;
      this.searchQuery = this.labelFor(this.modelValue);
    },
    scrollHighlightedIntoView() {
      const list = this.$el.querySelector(".wl-state-list");
      if (!list) return;
      const el = list.querySelector("li.is-highlighted");
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ block: "nearest" });
      }
    }
  }
};
</script>

<style scoped>
.wl-state-picker {
  position: relative;
  width: 100%;
  margin-bottom: 40px;
}

/* Input matches .select-field-3.form.update */
.wl-state-input {
  display: block;
  width: 100%;
  height: 50px;
  padding: 0 36px 0 12px;
  border: 1px solid #000;
  border-radius: 6px;
  background-color: #fff;
  color: #1a1a1a;
  font-family: var(--satoshi-switch), Manrope, sans-serif;
  font-size: 14px;
  line-height: 20px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.wl-state-input::placeholder {
  color: #696969;
}

.wl-state-input:focus {
  border-color: #083047;
}

.wl-state-input.field-error {
  border-color: #e34141;
  box-shadow: 0 0 0 3px rgba(227, 65, 65, 0.12);
}

.wl-state-chevron {
  position: absolute;
  right: 12px;
  top: 25px;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  stroke: #666;
  pointer-events: none;
}

@media screen and (max-width: 767px) {
  .wl-state-input {
    height: 60px;
  }
  .wl-state-chevron {
    top: 30px;
  }
}

@media screen and (max-width: 479px) {
  .wl-state-input {
    font-size: 16px;
  }
}

/* Dropdown */
.wl-state-list {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 50;
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 260px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d6d6d6;
  border-radius: 6px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  font-family: var(--satoshi-switch), Manrope, sans-serif;
}

.wl-state-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  color: #1a1a1a;
  transition: background-color 0.12s;
}

.wl-state-list li.is-highlighted,
.wl-state-list li:hover {
  background: #f3f6fa;
}

.wl-state-list li.is-selected {
  background: #e8f4fc;
}

.wl-state-name {
  flex: 1;
}

.wl-state-abbrev {
  font-size: 12px;
  font-weight: 500;
  color: #666;
}

.wl-state-empty {
  padding: 14px;
  text-align: center;
  color: #888;
  font-size: 13px;
  cursor: default;
}

.wl-state-empty:hover {
  background: transparent;
}
</style>
